"use client"

import { useState } from "react"
import type React from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Upload, Loader2, CheckCircle, X } from "lucide-react"
import { api } from "@/lib/api"

export function UploadTab() {
  const router = useRouter()
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [evalSuiteId, setEvalSuiteId] = useState("")
  const [agentVersion, setAgentVersion] = useState("")
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
      file.name.endsWith(".wav") ||
      file.name.endsWith(".mp3")
    )
    const updatedFiles = [...files, ...droppedFiles].slice(0, 100)
    setFiles(updatedFiles)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files).filter((file) =>
        file.name.endsWith(".wav") ||
        file.name.endsWith(".mp3")
      )
      const updatedFiles = [...files, ...selectedFiles].slice(0, 100)
      setFiles(updatedFiles)
    }
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i]
  }

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Please select at least one audio file")
      return
    }

    if (!evalSuiteId.trim() || !agentVersion.trim()) {
      setError("Please fill in all required fields")
      return
    }

    setUploading(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await api.uploadBatch(evalSuiteId, agentVersion, files)
      setSuccess(true)
      
      setTimeout(() => {
        router.push(`/batch/${response.batch_id}`)
      }, 1500)
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to upload batch. Please try again.")
      setUploading(false)
    }
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-foreground">Upload Call Recordings</h1>
          <p className="text-lg text-muted-foreground">Evaluate AI agent performance from audio files</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="suite-id">Owner <span className="text-destructive">*</span></Label>
            <Input 
              id="suite-id" 
              placeholder="Enter owner name" 
              value={evalSuiteId}
              onChange={(e) => setEvalSuiteId(e.target.value)}
              disabled={uploading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agent-version">Agent Version <span className="text-destructive">*</span></Label>
            <Input 
              id="agent-version" 
              placeholder="e.g. Anika v1.2" 
              value={agentVersion}
              onChange={(e) => setAgentVersion(e.target.value)}
              disabled={uploading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Upload Audio Files <span className="text-destructive">*</span></Label>
          <div
            className={`relative rounded-lg border-2 border-dashed transition-all ${
              isDragging ? "border-primary bg-primary/5" : "border-border bg-muted/20"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              multiple
              accept=".wav,.mp3"
              onChange={handleFileSelect}
              disabled={uploading}
              className="absolute inset-0 z-10 cursor-pointer opacity-0"
            />
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <Upload
                className={`mb-4 h-12 w-12 transition-colors ${isDragging ? "text-primary" : "text-muted-foreground"}`}
              />
              <p className="text-sm text-muted-foreground mb-1">Click to upload or drag and drop.</p>
              <p className="text-sm text-muted-foreground">Only .mp3 and .wav files are supported</p>
              {files.length > 0 && (
                <p className="mt-4 text-sm font-medium text-primary">
                  {files.length} file{files.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          </div>

          {files.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto rounded-lg border p-4">
              <h4 className="text-sm font-semibold">Selected Files</h4>
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-700">Batch uploaded successfully! Redirecting to results...</p>
          </div>
        )}

        <Button 
          className="w-full gradient-primary" 
          size="lg"
          onClick={handleUpload}
          disabled={uploading || success || files.length === 0}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Uploading...
            </>
          ) : success ? (
            <>
              <CheckCircle className="mr-2 h-5 w-5" />
              Success!
            </>
          ) : (
            <>
              <Upload className="mr-2 h-5 w-5" />
              Upload & Analyze
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
